import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SmokehouseSite from './pages/SmokehouseSite';
import CateringOffert from './pages/CateringOffert';
import SeoLandingPage from './pages/SeoLandingPage';
import { getCurrentUser } from './lib/api';
import { isPublicShopEnabled } from './lib/config';
import { trackPageView } from './lib/googleAnalytics';
import { useSEO } from './lib/seo';
import HardcodedValueWarning from './components/HardcodedValueWarning';

// Lazy-laddade sidor — admin/portal/login ska inte ligga i main-bundlen
// som publika besökare laddar.
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Login = lazy(() => import('./pages/Login'));
const AFLogin = lazy(() => import('./pages/AFLogin'));
const AFPortal = lazy(() => import('./pages/AFPortal'));
const AFApply = lazy(() => import('./pages/AFApply'));
const AFPasswordReset = lazy(() => import('./pages/AFPasswordReset'));
const Resellers = lazy(() => import('./pages/Resellers'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

function AnalyticsPageViews() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
}

// Validerar ÅF-token mot /api/af/me innan portalen visas. Bara 401/403 loggar ut;
// nätverksfel och 5xx behåller sessionen och erbjuder ett nytt försök.
function AFPortalRoute() {
  useSEO({ robots: 'noindex,nofollow', path: '/af' });
  const [state, setState] = useState(localStorage.getItem('af_token') ? 'checking' : 'login');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('af_token');
    if (!token) {
      setState('login'); // även när en omkontroll hittar att sessionen loggats ut i en annan flik
      return undefined;
    }
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/af/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setState('ok');
        } else if (res.status === 401 || res.status === 403) {
          // Ett sent nej för en token som redan ersatts (ny inloggning i annan flik) raderar
          // inte den nya sessionen; den nya kontrolleras i stället.
          if (localStorage.getItem('af_token') !== token) {
            setAttempt((a) => a + 1);
            return;
          }
          localStorage.removeItem('af_token');
          localStorage.removeItem('af_company');
          setState('login');
        } else {
          setState('error');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (state === 'checking') return null;
  if (state === 'login') return <Navigate to="/af/login" replace />;
  if (state === 'error') {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p>Kunde inte nå servern just nu. Du är fortfarande inloggad.</p>
        <button
          type="button"
          onClick={() => { setState('checking'); setAttempt((a) => a + 1); }}
          className="px-4 py-2 bg-amber-500 text-black rounded-lg font-semibold hover:bg-amber-400"
        >
          Försök igen
        </button>
      </div>
    );
  }
  return <AFPortal />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const publicShopEnabled = isPublicShopEnabled();

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        localStorage.removeItem('admin_token');
      }
      setUser(currentUser);
    } catch (error) {
      localStorage.removeItem('admin_token');
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-amber-400 text-xl font-light tracking-widest">Laddar...</div>
      </div>
    );
  }

  return (
    <Router future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}>
      <div className="App">
        <HardcodedValueWarning />
        <AnalyticsPageViews />
        <Suspense fallback={<div className="min-h-screen bg-black flex items-center justify-center text-amber-400 text-xl font-light tracking-widest">Laddar...</div>}>
          <Routes>
            <Route path="/" element={<SmokehouseSite />} />
            <Route path="/shop" element={publicShopEnabled ? <SmokehouseSite /> : <Navigate to="/" replace />} />
            <Route path="/produkter" element={<SmokehouseSite />} />
            <Route path="/produkter-v2" element={<Navigate to="/produkter" replace />} />
            <Route path="/produkter/:slug" element={<ProductDetail />} />
            <Route path="/order" element={<SmokehouseSite />} />
            <Route path="/jul" element={<Navigate to="/order" replace />} />
            <Route path="/bestall" element={<Navigate to="/order" replace />} />
            <Route path="/bestall/" element={<Navigate to="/order" replace />} />
            <Route path="/catering" element={<CateringOffert />} />
            <Route path="/pulled-pork" element={<SeoLandingPage section="pulled_pork_page" path="/pulled-pork" />} />
            <Route path="/brisket" element={<SeoLandingPage section="brisket_page" path="/brisket" />} />
            <Route path="/kontakt" element={<SmokehouseSite />} />
            <Route path="/batcher" element={<Navigate to="/" replace />} />
            <Route path="/catering/offert-preview" element={<CateringOffert />} />
            <Route
              path="/login"
              element={<Login setUser={setUser} />}
            />
            <Route
              path="/admin"
              element={
                user ? <AdminDashboard user={user} /> : <Login setUser={setUser} />
              }
            />
            <Route path="/butiker" element={<Resellers />} />
            <Route path="/aterforsaljare" element={<Navigate to="/butiker" replace />} />
            <Route path="/aterforsaljare/" element={<Navigate to="/butiker" replace />} />
            <Route path="/af/login" element={<AFLogin />} />
            <Route path="/af/ansok" element={<AFApply />} />
            <Route path="/af/glomt-losenord" element={<AFPasswordReset />} />
            <Route path="/af/aterstall" element={<AFPasswordReset />} />
            <Route
              path="/af"
              element={<AFPortalRoute />}
            />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
